import React from 'react';
import { 
  HiCheckCircle, 
  HiClock, 
  HiExclamationCircle,
  HiXCircle,
  HiArrowPath,
  HiPaperAirplane
} from 'react-icons/hi2';
import { cn } from '../../../utils/formatter';
import type { Feriado, Demanda } from '../../../models';
import { HOLIDAY_COLORS, DEMAND_COLORS } from './LegendPanel';

interface EventCardProps {
  event: {
    type: 'holiday' | 'demand';
    data: Feriado | Demanda;
  };
  compact?: boolean; // For month view
}

const getHolidayTooltip = (feriado: Feriado) => {
  const tipoLabels = {
    NACIONAL: 'Feriado Nacional',
    ESTADUAL: 'Feriado Estadual',
    MUNICIPAL: 'Feriado Municipal',
    FOLGA: 'Folga'
  };
  
  return `${feriado.descricao}\n${tipoLabels[feriado.tipo]}`;
};

const getDemandTooltip = (demanda: Demanda) => {
  const parts = [
    `Título: ${demanda.titulo}`,
    `Status: ${demanda.status}`,
    `Prioridade: ${demanda.prioridade}`,
  ];
  
  if (demanda.setor?.nome_setor) {
    parts.push(`Setor: ${demanda.setor.nome_setor}`);
  } else if (demanda.processo?.setor?.nome_setor) {
    parts.push(`Setor: ${demanda.processo.setor.nome_setor}`);
  }
  
  if (demanda.demandante?.name) {
    parts.push(`Demandante: ${demanda.demandante.name}`);
  }
  
  return parts.join('\n');
};

export const EventCard: React.FC<EventCardProps> = ({ event, compact = false }) => {
  if (event.type === 'holiday') {
    const feriado = event.data as Feriado;
    const colors = HOLIDAY_COLORS[feriado.tipo];
    
    const tipoLabels = {
      NACIONAL: 'Nacional',
      ESTADUAL: 'Estadual',
      MUNICIPAL: 'Municipal',
      FOLGA: 'Folga'
    };

    if (compact) {
      // Compact version for month view
      return (
        <div
          className={cn(
            'w-full h-full px-2 py-1 rounded cursor-pointer hover:shadow-sm transition-all flex items-center gap-1',
            colors.bg,
            colors.text
          )}
          title={getHolidayTooltip(feriado)}
        >
          <div className="font-medium text-xs truncate">
            {feriado.descricao}
          </div>
        </div>
      );
    }

    return (
      <div
        className={cn(
          'w-full h-full p-3 rounded-lg cursor-pointer hover:shadow-md transition-all flex flex-col gap-1',
          colors.bg,
          colors.text
        )}
        title={getHolidayTooltip(feriado)}
      >
        <div className="font-semibold text-sm line-clamp-2">
          {feriado.descricao}
        </div>
        <div className="text-xs opacity-80">
          {tipoLabels[feriado.tipo]}
        </div>
      </div>
    );
  }

  if (event.type === 'demand') {
    const demanda = event.data as Demanda;

    // Priority badge color
    const priorityColors = {
      'Urgente': 'bg-red-500',
      'Alta': 'bg-orange-500',
      'Media': 'bg-yellow-500',
      'Baixa': 'bg-gray-400'
    };
    
    // Status icons and colors
    const getStatusIcon = (status?: string, isCompact = false) => {
      const iconClass = isCompact ? "w-3 h-3 flex-shrink-0" : "w-4 h-4 flex-shrink-0";
      switch (status) {
        case 'FINALIZADA':
          return <HiCheckCircle className={cn(iconClass, 'text-green-600 dark:text-green-400')} />;
        case 'SOLUCIONADA':
          return <HiCheckCircle className={cn(iconClass, 'text-blue-600 dark:text-blue-400')} />;
        case 'EMPROGRESSO':
          return <HiArrowPath className={cn(iconClass, 'text-yellow-600 dark:text-yellow-400')} />;
        case 'RECEBIDA':
        case 'ATRIBUIDA':
          return <HiPaperAirplane className={cn(iconClass, 'text-purple-600 dark:text-purple-400')} />;
        case 'EMATRIBUICAO':
          return <HiClock className={cn(iconClass, 'text-gray-600 dark:text-gray-400')} />;
        case 'DEVOLVIDA':
          return <HiArrowPath className={cn(iconClass, 'text-orange-600 dark:text-orange-400')} />;
        case 'ERRADA':
        case 'RECUSADA':
        case 'EXCLUIDA':
          return <HiXCircle className={cn(iconClass, 'text-red-600 dark:text-red-400')} />;
        default:
          return <HiExclamationCircle className={cn(iconClass, 'text-gray-600 dark:text-gray-400')} />;
      }
    };
    
    const statusLabels: Record<string, string> = {
      'EMATRIBUICAO': 'Em Atribuição',
      'RECEBIDA': 'Recebida',
      'ATRIBUIDA': 'Atribuída',
      'EMPROGRESSO': 'Em Progresso',
      'SOLUCIONADA': 'Solucionada',
      'FINALIZADA': 'Finalizada',
      'DEVOLVIDA': 'Devolvida',
      'ERRADA': 'Errada',
      'RECUSADA': 'Recusada',
      'EXCLUIDA': 'Excluída'
    };

    if (compact) {
      // Compact version for month view
      return (
        <div
          className={cn(
            'w-full h-full px-2 py-1 rounded cursor-pointer hover:shadow-sm transition-all flex items-center gap-1.5',
            DEMAND_COLORS.bg,
            DEMAND_COLORS.text
          )}
          title={getDemandTooltip(demanda)}
        >
          {getStatusIcon(demanda.status, true)}
          <div className="flex-1 min-w-0 font-medium text-xs truncate">
            {demanda.titulo}
          </div>
          <span className={cn(
            'w-1.5 h-1.5 rounded-full flex-shrink-0',
            priorityColors[demanda.prioridade]
          )} title={`Prioridade: ${demanda.prioridade}`} />
        </div>
      );
    }

    return (
      <div
        className={cn(
          'w-full h-full p-3 rounded-lg cursor-pointer hover:shadow-md transition-all flex flex-col gap-2',
          DEMAND_COLORS.bg,
          DEMAND_COLORS.text
        )}
        title={getDemandTooltip(demanda)}
      >
        {/* Header with Status Icon and Title */}
        <div className="flex items-start gap-2">
          {getStatusIcon(demanda.status)}
          <div className="flex-1 min-w-0">
            <div className="font-semibold text-sm line-clamp-2">
              {demanda.titulo}
            </div>
          </div>
          <span className={cn(
            'w-2 h-2 rounded-full flex-shrink-0 mt-1.5',
            priorityColors[demanda.prioridade]
          )} title={`Prioridade: ${demanda.prioridade}`} />
        </div>
        
        {/* Details */}
        <div className="flex flex-col gap-1 text-xs opacity-80">
          <div className="flex items-center gap-2">
            <span className="font-medium">Status:</span>
            <span>{statusLabels[demanda.status || ''] || demanda.status}</span>
          </div>
          {(demanda.setor?.nome_setor || demanda.processo?.setor?.nome_setor) && (
            <div className="flex items-center gap-2">
              <span className="font-medium">Setor:</span>
              <span className="truncate">{demanda.setor?.nome_setor || demanda.processo?.setor?.nome_setor}</span>
            </div>
          )}
        </div>
      </div>
    );
  }

  return null;
};

